import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "../../../lib/utils.js";
import { Button } from "../../ui/button.js";
import { GlowCard } from "../../ui/glow-card.js";
import { Skeleton } from "../../ui/skeleton.js";
import { useToast } from "../../../context/ToastContext.js";
import { useTodoActions, useTodosRange } from "../../../lib/useCalendarTodos.js";
import { CATEGORY_LABEL, buildDayView, categoryOf, todoDateKey, type CalendarTodo, type TodoCategory } from "../../../lib/calendarTodos.js";
import { CATEGORY_STYLE, formatTime } from "../../calendar/todoStyles.js";
import type { WidgetProps } from "../widget-registry.js";
import { CategoryLegend, FOCUS, MonthGrid, QuickAdd, TickButton, WeekStrip } from "./MyTodosParts.js";
import {
  addDays, calendarLink, countsFor, dayLabel, defaultSelection, isoDate, monthLabel, msUntilMidnight, neighbourRanges,
  parseIso, periodsAgo, rangeFor, relativeLabel, selectionAfterNav, stepPeriod, weekLabel, weekdayNamesSun,
  type PeriodMode,
} from "./my-todos-calendar.js";

const COUNT_STYLE = { open: "text-brand-600", done: "text-success", overdue: "text-error" } as const;
const PANEL_ROWS = 3;

export function MyTodosWidget(_props: WidgetProps) {
  const toast = useToast();
  const [today, setToday] = useState(() => new Date());
  const todayIso = isoDate(today);

  const [mode, setMode] = useState<PeriodMode>("week");
  const [anchor, setAnchor] = useState<Date>(today);
  const [selected, setSelected] = useState<string>(todayIso);
  const [fading, setFading] = useState(false);
  const [announce, setAnnounce] = useState("");
  const gridRef = useRef<HTMLDivElement>(null);
  const focusAfter = useRef(false);

  // Local midnight: roll "today" over so the highlight and the overdue counts stay right.
  useEffect(() => {
    const t = window.setTimeout(() => setToday(new Date()), msUntilMidnight());
    return () => window.clearTimeout(t);
  }, [todayIso]);

  const range = useMemo(() => rangeFor(anchor, mode), [anchor, mode]);
  const neighbours = useMemo(() => neighbourRanges(anchor, mode), [anchor, mode]);
  const { todos: all, loading, loadError, ready, reload } = useTodosRange(range.from, range.to, true, neighbours);
  const actions = useTodoActions((m) => { toast.show(m, "error"); setAnnounce("Couldn't save"); });

  const ago = periodsAgo(anchor, mode, today);
  const isCurrent = ago === 0;
  const periodTodos = useMemo(() => all.filter(t => { const k = todoDateKey(t); return k >= range.from && k <= range.to; }), [all, range]);
  const counts = useMemo(() => countsFor(periodTodos, range, todayIso), [periodTodos, range, todayIso]);
  const weekdays = useMemo(() => weekdayNamesSun(), []);
  const showSkeleton = loading && !ready;

  const withFade = (fn: () => void) => { setFading(true); fn(); window.setTimeout(() => setFading(false), 220); };

  function switchMode(next: PeriodMode) {
    if (next === mode) return;
    withFade(() => { setMode(next); setAnchor(today); setSelected(todayIso); });
  }

  const step = useCallback((delta: number) => {
    const nextAnchor = stepPeriod(anchor, mode, delta);
    setAnchor(nextAnchor);
    setSelected(sel => selectionAfterNav(sel, rangeFor(nextAnchor, mode), todayIso));
  }, [anchor, mode, todayIso]);

  function jumpToCurrent() {
    withFade(() => { setAnchor(today); setSelected(todayIso); });
  }

  /** Arrow keys walk the days, Home/End hit the week's edges, PageUp/PageDown change the period. */
  function onGridKeyDown(e: React.KeyboardEvent) {
    const move = (to: string) => {
      if (to < range.from || to > range.to) return;
      e.preventDefault();
      setSelected(to);
      focusAfter.current = true;
    };
    const cur = parseIso(selected);
    switch (e.key) {
      case "ArrowLeft": move(isoDate(addDays(cur, -1))); break;
      case "ArrowRight": move(isoDate(addDays(cur, 1))); break;
      case "ArrowUp": if (mode === "month") move(isoDate(addDays(cur, -7))); break;
      case "ArrowDown": if (mode === "month") move(isoDate(addDays(cur, 7))); break;
      case "Home": { const t = isoDate(addDays(cur, -cur.getDay())); move(t < range.from ? range.from : t); break; }
      case "End": { const t = isoDate(addDays(cur, 6 - cur.getDay())); move(t > range.to ? range.to : t); break; }
      case "PageUp": e.preventDefault(); step(-1); focusAfter.current = true; break;
      case "PageDown": e.preventDefault(); step(1); focusAfter.current = true; break;
    }
  }

  useEffect(() => {
    if (!focusAfter.current) return;
    focusAfter.current = false;
    gridRef.current?.querySelector<HTMLElement>(`[data-day="${selected}"]`)?.focus();
  }, [selected, range]);

  const dayTodos = useMemo(() => buildDayView(periodTodos.filter(t => todoDateKey(t) === selected), selected, todayIso), [periodTodos, selected, todayIso]);
  const isPast = selected < todayIso;

  const onToggle = useCallback((id: string, done: boolean) => {
    void actions.setDone(id, done).then(() => setAnnounce(done ? "Marked done" : "Marked not done")).catch(() => {});
  }, [actions]);

  const onAdd = useCallback(async (title: string, category: TodoCategory) => {
    await actions.create({ title, description: "", category, tags: [], time: null, date: selected });
    setAnnounce("Todo added");
  }, [actions, selected]);

  const label = mode === "week" ? weekLabel(range.from, range.to) : monthLabel(anchor);
  const shortDate = (() => { const d = parseIso(selected); return `${d.getDate()} ${d.toLocaleDateString(undefined, { month: "short" })}`; })();
  const noun = mode === "week" ? "week" : "month";

  return (
    <GlowCard glow="ultra" spotlight="off" size="none" radius="2xl" className="@container h-full p-[18px]">
      <div role="group" aria-label="My todos">
        {/* 1 ── Header */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <h3 className="text-[15px] font-bold text-surface-900">My Todos</h3>
          <div role="radiogroup" aria-label="Period" className="flex gap-1 rounded-lg bg-surface-100 p-0.5">
            {(["week", "month"] as const).map(m => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={mode === m}
                onClick={() => switchMode(m)}
                className={cn(
                  "h-[26px] rounded-md px-2.5 text-[11px] font-semibold capitalize transition-colors motion-reduce:transition-none @max-[360px]:px-2 @max-[360px]:text-[10px]",
                  FOCUS,
                  mode === m ? "bg-brand-600 text-white hover:bg-brand-700" : "text-surface-600 hover:text-surface-900",
                )}
              >
                {m === "week" ? "Week" : "Month"}
              </button>
            ))}
          </div>
        </div>

        {/* 2 ── Period navigation (unbounded both ways: todos can be in the future) */}
        <div className="mb-3 flex items-center gap-1.5">
          <Button type="button" variant="secondary" size="icon" className="h-7 w-7 shrink-0" onClick={() => step(-1)} aria-label={`Previous ${noun}`}>
            <ChevronLeft size={15} />
          </Button>
          <div className="flex min-w-0 flex-1 items-baseline justify-center gap-1.5" aria-live="polite">
            <span className="truncate whitespace-nowrap text-[14px] font-bold text-surface-900">{label}</span>
            <span className="hidden whitespace-nowrap text-[11px] text-surface-500 @[340px]:inline">{relativeLabel(ago, mode)}</span>
          </div>
          {!isCurrent && (
            <Button type="button" variant="ghost" size="sm" className="h-7 shrink-0 whitespace-nowrap px-2 text-[11px] text-brand-600" onClick={jumpToCurrent}>
              This {noun}
            </Button>
          )}
          <Button type="button" variant="secondary" size="icon" className="h-7 w-7 shrink-0" onClick={() => step(1)} aria-label={`Next ${noun}`}>
            <ChevronRight size={15} />
          </Button>
        </div>

        {/* 3 ── Counts */}
        <div className="mb-3 grid grid-cols-3 gap-2 @max-[360px]:mb-2 @max-[360px]:gap-1.5">
          {([["OPEN", "open", "OPEN"], ["DONE", "done", "DONE"], ["OVERDUE", "overdue", "LATE"]] as const).map(([text, k, short]) => (
            <div key={k} className="flex items-center justify-between gap-1 rounded-[10px] bg-surface-100 px-2.5 py-1.5 @max-[360px]:flex-col @max-[360px]:items-start @max-[360px]:gap-0">
              <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-surface-500">
                <span className="@max-[360px]:hidden">{text}</span><span className="hidden @max-[360px]:inline">{short}</span>
              </span>
              {showSkeleton
                ? <Skeleton className="h-[22px] w-6" />
                : <span className={cn("text-[22px] font-bold leading-none @max-[360px]:text-[17px]", counts[k] > 0 ? COUNT_STYLE[k] : "text-surface-400")}>{counts[k]}</span>}
            </div>
          ))}
        </div>

        {/* 4 ── Main area: fixed height so Week/Month and navigation never resize the card */}
        <div
          ref={gridRef}
          onKeyDown={onGridKeyDown}
          className={cn(
            "flex h-[196px] flex-col justify-center transition-opacity duration-200 motion-reduce:transition-none @max-[360px]:h-[146px]",
            fading ? "opacity-0" : "opacity-100",
          )}
        >
          {mode === "week" ? (
            <WeekStrip range={range} todos={showSkeleton ? [] : periodTodos} selected={selected} todayIso={todayIso} onSelect={setSelected} />
          ) : (
            <MonthGrid range={range} todos={showSkeleton ? [] : periodTodos} selected={selected} todayIso={todayIso} weekdays={weekdays} onSelect={setSelected} />
          )}
        </div>

        {/* 5 ── Legend */}
        <CategoryLegend />

        {/* 6 ── Selected day */}
        <div className="mt-3 rounded-xl border border-surface-200 bg-surface-100 p-3">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 text-[13px] font-bold text-surface-900">{dayLabel(selected)}</span>
              {selected === todayIso && <span className="shrink-0 rounded-full bg-brand-100 px-2 py-0.5 text-[10px] font-semibold text-brand-700">Today</span>}
            </div>
            <span className="truncate text-[11px] text-surface-500">
              {dayTodos.total === 0 ? "No todos" : `${dayTodos.openCount} open · ${dayTodos.doneCount} done`}
            </span>
          </div>

          {/* Fixed: 3 rows x 34px + the "+N more" line, so a busy day never makes the card taller. */}
          <div className="h-[120px] overflow-hidden">
            {loadError ? (
              <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
                <p className="text-[12px] text-surface-500">Couldn't load your todos.</p>
                <button type="button" onClick={() => void reload()} className={cn("rounded text-[12px] font-semibold text-brand-600 hover:underline", FOCUS)}>Retry</button>
              </div>
            ) : showSkeleton ? (
              <div className="space-y-2 pt-1" aria-hidden="true">
                {Array.from({ length: PANEL_ROWS }, (_, i) => <Skeleton key={i} className="h-[26px] w-full" />)}
              </div>
            ) : dayTodos.total === 0 ? (
              <p className="flex h-full items-center justify-center text-center text-[12px] text-surface-500">Nothing planned. Add a todo below.</p>
            ) : (
              <>
                {dayTodos.sorted.slice(0, PANEL_ROWS).map((t: CalendarTodo) => {
                  const cat = categoryOf(t.tone);
                  return (
                    <div key={t.id} className="flex h-[34px] animate-td-row items-center gap-2.5">
                      <TickButton todo={t} onToggle={onToggle} />
                      <Link
                        to={calendarLink(selected, t.id)}
                        className={cn("min-w-0 flex-1 truncate text-[13px] no-underline hover:underline", t.done ? "text-surface-400 line-through" : "text-surface-900", FOCUS)}
                      >
                        {t.title}
                      </Link>
                      <span className="flex shrink-0 items-center gap-1 text-[11px] text-surface-500 @max-[300px]:hidden">
                        <i className={cn("size-1.5 rounded-full", CATEGORY_STYLE[cat].dot)} />
                        {CATEGORY_LABEL[cat]}{t.time ? ` · ${formatTime(t.time)}` : ""}
                      </span>
                    </div>
                  );
                })}
                {dayTodos.total > PANEL_ROWS && (
                  <Link to={calendarLink(selected)} className={cn("block h-[18px] rounded text-[11px] font-bold leading-[18px] text-brand-600 no-underline hover:underline", FOCUS)}>
                    +{dayTodos.total - PANEL_ROWS} more · open the calendar
                  </Link>
                )}
              </>
            )}
          </div>

          <QuickAdd dateLabel={shortDate} disabledReason={isPast ? "Can't add to a past day" : undefined} onAdd={onAdd} />
        </div>

        {/* 7 ── Footer */}
        <div className="mt-2 text-right">
          <Link to={calendarLink(selected)} className={cn("rounded text-[11px] font-semibold text-brand-600 no-underline hover:underline", FOCUS)}>
            Open calendar →
          </Link>
        </div>
        <div className="sr-only" role="status" aria-live="polite">{announce}</div>
      </div>
    </GlowCard>
  );
}
